import React, { useState } from 'react';
import { X, Linkedin, Building2, Eye, EyeOff } from 'lucide-react';

const LinkedInConnectionModal = ({ onClose, onSuccess }) => {
  const [accountType, setAccountType] = useState('personal');
  const [linkedinEmail, setLinkedinEmail] = useState('');
  const [linkedinPassword, setLinkedinPassword] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleConnect = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const userId = localStorage.getItem('userId');

      console.log('🔵 Connecting LinkedIn with:', {
        userId,
        linkedinEmail,
        accountType,
        companyId,
        companyName
      });

      const response = await fetch('https://n8n.acespireconsulting.com/webhook/connect-linkedin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: parseInt(userId),
          linkedinEmail,
          linkedinPassword,
          accountType,
          companyId: accountType === 'company' ? companyId : null,
          companyName: accountType === 'company' ? companyName : null
        })
      });

      console.log('🔵 Response status:', response.status);
      console.log('🔵 Response headers:', [...response.headers.entries()]);

      // ✅ CRITICAL: Get raw response text first
      const responseText = await response.text();
      console.log('🔵 Raw response:', responseText);

      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error('❌ JSON parse error:', parseError);
        console.error('Response was:', responseText);
        setError('Invalid response from server');
        setLoading(false);
        return;
      }

      console.log('🔵 Parsed response:', result);
      console.log('🔵 Response structure:', JSON.stringify(result, null, 2));

      if (response.ok && result.success) {
        // ✅ N8N returns array, get first element
        const data = Array.isArray(result) ? result[0] : result;
        
        console.log('🔵 Data object:', data);
        
        // ✅ Extract accountId - check multiple possible locations
        let accountId = data.linkedin_account_id || 
                       data.linkedinAccountId || 
                       data.data?.linkedin_account_id || 
                       data.data?.linkedinAccountId;
        
        let accountTypeValue = data.account_type || 
                              data.accountType || 
                              data.data?.account_type || 
                              data.data?.accountType || 
                              accountType;

        console.log('🔵 Extracted accountId:', accountId);
        console.log('🔵 Extracted accountType:', accountTypeValue);
        
        if (!accountId) {
          console.error('❌ ERROR: No accountId in response!');
          console.error('Full response structure:', result);
          
          // ✅ FALLBACK: Fetch from database
          console.log('⚠️ Attempting to fetch accountId from database...');
          
          try {
            const accountsResponse = await fetch('https://n8n.acespireconsulting.com/webhook/get-linkedin-accounts', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ userId: parseInt(userId) })
            });
            
            const accountsText = await accountsResponse.text();
            const accountsData = JSON.parse(accountsText);
            
            console.log('🔵 Fetched accounts:', accountsData);
            
            if (accountsData.success && accountsData.accounts) {
              const newAccount = accountsData.accounts.find(
                a => a.linkedin_username === linkedinEmail && a.account_type === accountType
              );
              
              if (newAccount) {
                accountId = newAccount.linkedin_account_id;
                accountTypeValue = newAccount.account_type;
                console.log('✅ Found accountId from database:', accountId);
              }
            }
          } catch (fetchError) {
            console.error('❌ Error fetching accounts:', fetchError);
          }
          
          if (!accountId) {
            setError('Account connected but could not retrieve account ID. Please refresh the page.');
            setLoading(false);
            return;
          }
        }
        
        // ✅ Store in localStorage
        localStorage.setItem('selectedLinkedInAccountId', accountId.toString());
        localStorage.setItem('selectedAccountType', accountTypeValue);
        localStorage.setItem('linkedinStatus', 'connected');
        localStorage.setItem('linkedinEmail', linkedinEmail);
        
        console.log('✅ LinkedIn connected - Data stored:', {
          accountId,
          accountType: accountTypeValue,
          status: 'connected',
          email: linkedinEmail
        });
        
        alert('LinkedIn account connected successfully!');
        
        console.log('🔵 Calling onSuccess with accountId:', accountId);
        onSuccess(accountId);
        
      } else {
        console.error('❌ Connection failed:', result.message);
        setError(result.message || 'Failed to connect LinkedIn account');
      }
    } catch (error) {
      console.error('❌ Error connecting LinkedIn:', error);
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Linkedin className="w-6 h-6 text-blue-600 mr-2" />
            <h2 className="text-xl font-semibold">Connect LinkedIn</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-6">
          Connect your LinkedIn account to schedule and post content directly.
        </p>
        {/* 🔔 LinkedIn Verification Note */}
<div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-gray-600">
  <strong>Note:</strong> LinkedIn may require manual verification during login.
  Please open the LinkedIn mobile app and tap <strong>“Yes, it’s me”</strong> to complete the login process.
</div>

        <form onSubmit={handleConnect}>
          {/* Account Type */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Account Type
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setAccountType('personal')}
                className={`flex items-center justify-center px-4 py-3 border-2 rounded-lg transition-colors ${
                  accountType === 'personal'
                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Linkedin className="w-4 h-4 mr-2" />
                Personal
              </button>
              <button
                type="button"
                onClick={() => setAccountType('company')}
                className={`flex items-center justify-center px-4 py-3 border-2 rounded-lg transition-colors ${
                  accountType === 'company'
                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Building2 className="w-4 h-4 mr-2" />
                Company
              </button>
            </div>
          </div>

          {/* LinkedIn Email */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              LinkedIn Email
            </label>
            <input
              type="email"
              value={linkedinEmail}
              onChange={(e) => setLinkedinEmail(e.target.value)}
              placeholder="your.email@example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {/* LinkedIn Password */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              LinkedIn Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={linkedinPassword}
                onChange={(e) => setLinkedinPassword(e.target.value)}
                placeholder="Enter your password"
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Company Fields (only if company account) */}
          {accountType === 'company' && (
            <>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company ID
                </label>
                <input
                  type="text"
                  value={companyId}
                  onChange={(e) => setCompanyId(e.target.value)}
                  placeholder="e.g., 12345678 or company-slug"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Find this in your LinkedIn company page URL
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="e.g., Acespire Consulting"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
            </>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
              {error}
            </div>
          )}

          {/* Buttons */}
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Connecting...' : 'Connect'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LinkedInConnectionModal;
// //import React, { useState } from 'react';
// import { X, Linkedin, Building2, Eye, EyeOff } from 'lucide-react';

// const LinkedInConnectionModal = ({ onClose, onSuccess }) => {
//   const [accountType, setAccountType] = useState('personal');
//   const [linkedinEmail, setLinkedinEmail] = useState('');
//   const [linkedinPassword, setLinkedinPassword] = useState('');
//   const [companyId, setCompanyId] = useState('');
//   const [companyName, setCompanyName] = useState('');
//   const [showPassword, setShowPassword] = useState(false);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState('');

//   const handleConnect = async (e) => {
//     e.preventDefault();
//     setError('');
//     setLoading(true);

//     try {
//       const userId = localStorage.getItem('userId');

//       const response = await fetch('https://n8n.acespireconsulting.com/webhook/connect-linkedin', {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json' },
//         body: JSON.stringify({
//           userId: parseInt(userId),
//           linkedinEmail,
//           linkedinPassword,
//           accountType,
//           companyId: accountType === 'company' ? companyId : null,
//           companyName: accountType === 'company' ? companyName : null
//         })
//       });

//       const data = await response.json();

//       if (response.ok && data.success) {
//         // Success - call onSuccess with the new account ID
//         localStorage.setItem('selectedLinkedInAccountId', data.linkedinAccountId);
//         localStorage.setItem('selectedAccountType', data.accountType);
        
//         alert('LinkedIn account connected successfully!');
//         onSuccess(data.linkedinAccountId);
//       } else {
//         setError(data.message || 'Failed to connect LinkedIn account');
//       }
//     } catch (error) {
//       console.error('Error connecting LinkedIn:', error);
//       setError('An error occurred. Please try again.');
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//       <div className="bg-white rounded-lg p-6 max-w-md w-full">
//         <div className="flex items-center justify-between mb-4">
//           <div className="flex items-center">
//             <Linkedin className="w-6 h-6 text-blue-600 mr-2" />
//             <h2 className="text-xl font-semibold">Connect LinkedIn</h2>
//           </div>
//           <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
//             <X className="w-5 h-5" />
//           </button>
//         </div>

//         <p className="text-sm text-gray-600 mb-6">
//           Connect your LinkedIn account to schedule and post content directly.
//         </p>

//         <form onSubmit={handleConnect}>
//           {/* Account Type */}
//           <div className="mb-4">
//             <label className="block text-sm font-medium text-gray-700 mb-2">
//               Account Type
//             </label>
//             <div className="grid grid-cols-2 gap-3">
//               <button
//                 type="button"
//                 onClick={() => setAccountType('personal')}
//                 className={`flex items-center justify-center px-4 py-3 border-2 rounded-lg transition-colors ${
//                   accountType === 'personal'
//                     ? 'border-blue-600 bg-blue-50 text-blue-700'
//                     : 'border-gray-200 hover:border-gray-300'
//                 }`}
//               >
//                 <Linkedin className="w-4 h-4 mr-2" />
//                 Personal
//               </button>
//               <button
//                 type="button"
//                 onClick={() => setAccountType('company')}
//                 className={`flex items-center justify-center px-4 py-3 border-2 rounded-lg transition-colors ${
//                   accountType === 'company'
//                     ? 'border-blue-600 bg-blue-50 text-blue-700'
//                     : 'border-gray-200 hover:border-gray-300'
//                 }`}
//               >
//                 <Building2 className="w-4 h-4 mr-2" />
//                 Company
//               </button>
//             </div>
//           </div>

//           {/* LinkedIn Email */}
//           <div className="mb-4">
//             <label className="block text-sm font-medium text-gray-700 mb-2">
//               LinkedIn Email
//             </label>
//             <input
//               type="email"
//               value={linkedinEmail}
//               onChange={(e) => setLinkedinEmail(e.target.value)}
//               placeholder="your.email@example.com"
//               className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
//               required
//             />
//           </div>

//           {/* LinkedIn Password */}
//           <div className="mb-4">
//             <label className="block text-sm font-medium text-gray-700 mb-2">
//               LinkedIn Password
//             </label>
//             <div className="relative">
//               <input
//                 type={showPassword ? 'text' : 'password'}
//                 value={linkedinPassword}
//                 onChange={(e) => setLinkedinPassword(e.target.value)}
//                 placeholder="Enter your password"
//                 className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
//                 required
//               />
//               <button
//                 type="button"
//                 onClick={() => setShowPassword(!showPassword)}
//                 className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
//               >
//                 {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
//               </button>
//             </div>
//           </div>

//           {/* Company Fields (only if company account) */}
//           {accountType === 'company' && (
//             <>
//               <div className="mb-4">
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Company ID
//                 </label>
//                 <input
//                   type="text"
//                   value={companyId}
//                   onChange={(e) => setCompanyId(e.target.value)}
//                   placeholder="e.g., 12345678 or company-slug"
//                   className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
//                   required
//                 />
//                 <p className="text-xs text-gray-500 mt-1">
//                   Find this in your LinkedIn company page URL
//                 </p>
//               </div>

//               <div className="mb-4">
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Company Name
//                 </label>
//                 <input
//                   type="text"
//                   value={companyName}
//                   onChange={(e) => setCompanyName(e.target.value)}
//                   placeholder="e.g., Acespire Consulting"
//                   className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
//                   required
//                 />
//               </div>
//             </>
//           )}

//           {/* Error Message */}
//           {error && (
//             <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
//               {error}
//             </div>
//           )}

//           {/* Buttons */}
//           <div className="flex space-x-3">
//             <button
//               type="button"
//               onClick={onClose}
//               className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
//               disabled={loading}
//             >
//               Cancel
//             </button>
//             <button
//               type="submit"
//               disabled={loading}
//               className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
//             >
//               {loading ? 'Connecting...' : 'Connect'}
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   );
// };

// export default LinkedInConnectionModal //